// Product Videos — adds a "Videos" tab to the storefront product-detail page
// showing the part's product videos (YouTube thumbnails / links), mirroring the
// Partsfinder detail view. Uses the SAME indexed part data (/api/parts/:sku →
// `videos`) and the SAME role gate (`showVideos`) as the React views, so
// behaviour is identical. Videos are hidden from non-logged-in (Public) visitors
// by role default.
//
// Config (read from the TARGET element, not the script tag):
//   #parts-videos  OR  [data-parts-videos]  — the container (optional; if absent
//     the widget injects its own "Videos" tab into the storefront plugin-tabs)
//   data-sku-selector="#product-sku"  (optional; overrides the SKU source)
//
// SKU source (first hit wins): data-sku-selector / #product-sku, then any
// [data-sku] on the page.

import { getTopDocument, getTopBody, getSku, esc } from '../shared/dom.js';
import { apiJson } from '../shared/api.js';
import { loadFeatures } from '../shared/identity.js';

// Extract a YouTube video id from youtu.be/ID, watch?v=ID, embed/ID, shorts/ID.
function ytId(url) {
  var m = String(url == null ? '' : url).match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? m[1] : '';
}

function render(container, videos) {
  var html = ''
    + '<div class="pfv-wrap">'
    + '<div class="pfv-header"><p class="pfv-title">Videos</p><p class="pfv-subtitle">Product videos. Click to watch</p></div>'
    + '<div class="pfv-grid">';
  videos.forEach(function (v) {
    var url = (v && v.url ? String(v.url) : '').trim();
    if (!url) return;
    var title = (v && v.title ? String(v.title) : 'Watch video').trim();
    var id = ytId(url);
    html += '<a class="pfv-card" href="' + esc(url) + '" target="_blank" rel="noopener noreferrer">';
    if (id) {
      html += '<span class="pfv-thumb" style="background-image:url(\'https://img.youtube.com/vi/' + esc(id) + '/hqdefault.jpg\')"><span class="pfv-play">▶</span></span>';
    } else {
      html += '<span class="pfv-thumb pfv-thumb-generic"><span class="pfv-play">▶</span></span>';
    }
    html += '<span class="pfv-cardtitle">' + esc(title) + '</span>';
    html += '</a>';
  });
  html += '</div></div>';
  container.innerHTML = html;
}

// Shown when the part has no videos, so the tab renders a message instead of a blank pane.
function renderEmpty(container) {
  container.innerHTML = ''
    + '<div class="pfv-wrap">'
    + '<div class="pfv-header"><p class="pfv-title">Videos</p></div>'
    + '<div class="pfv-empty">No videos found for this part.</div>'
    + '</div>';
}

/**
 * Create the "Videos" tab + panel, inserted after the Branch On Hand tab (or
 * the vehicle-compatibility tab). Mirrors storefront-branch-on-hand.js.
 */
function ensureTabContainer() {
  var topDoc = getTopDocument();
  var tabId = 'parts-videos';
  var existing = topDoc.getElementById(tabId);
  if (existing) return existing;

  var navTabsContain = topDoc.querySelector('.nav-tabs-contain');
  if (!navTabsContain) return null;

  var tabList = navTabsContain.querySelector('.nav.nav-tabs') || navTabsContain;
  var tabContent = topDoc.querySelector('.plugin-tab-content') || navTabsContain.querySelector('.tab-content');
  if (!tabContent) return null;

  var existingPane = tabContent.querySelector('[role="tabpanel"]');
  var paneClass = existingPane ? existingPane.className.replace(/\bactive\b/g, '').trim() : 'plugin-tab-pane';

  // Tab trigger button (matches .plugin-tab-btn pattern).
  var tabBtn = topDoc.createElement('button');
  tabBtn.type = 'button';
  tabBtn.className = 'plugin-tab-btn';
  tabBtn.setAttribute('data-tab', tabId);
  tabBtn.setAttribute('id', 'pfv-tab-btn');
  tabBtn.textContent = 'Videos';

  var afterTrigger = tabList.querySelector('[data-tab="pboh-branchonhand"]')
    || tabList.querySelector('[data-tab="pvc-vehiclecompatibility"]');
  if (afterTrigger && afterTrigger.nextSibling) {
    tabList.insertBefore(tabBtn, afterTrigger.nextSibling);
  } else {
    tabList.appendChild(tabBtn);
  }

  // Tab panel.
  var panel = topDoc.createElement('div');
  panel.setAttribute('role', 'tabpanel');
  panel.className = paneClass;
  panel.id = tabId;

  var afterPanel = tabContent.querySelector('#pboh-branchonhand')
    || tabContent.querySelector('#pvc-vehiclecompatibility');
  if (afterPanel && afterPanel.nextSibling) {
    tabContent.insertBefore(panel, afterPanel.nextSibling);
  } else {
    tabContent.appendChild(panel);
  }

  return panel;
}

/**
 * Resolve where to render: an explicit container on the page wins, else the
 * injected plugin-tabs panel, else a created div appended to the top body.
 * `explicit` is the already-looked-up [data-parts-videos] / #parts-videos node.
 */
function resolveContainer(explicit) {
  var topDoc = getTopDocument();
  var container = explicit
    || topDoc.getElementById('parts-videos')
    || topDoc.querySelector('[data-parts-videos]');
  if (!container) container = ensureTabContainer();
  if (!container) {
    container = topDoc.createElement('div');
    container.id = 'parts-videos';
    container.setAttribute('data-parts-videos', '');
    getTopBody().appendChild(container);
  }
  return container;
}

export function init() {
  var topDoc = getTopDocument();
  // Explicit container is optional — the widget can inject its own Videos tab.
  var explicit = topDoc.getElementById('parts-videos') || topDoc.querySelector('[data-parts-videos]');

  // Per-instance config now lives on the container (was on the <script> tag).
  var skuSelector = (explicit && explicit.getAttribute('data-sku-selector')) || '#product-sku';

  var sku = getSku(skuSelector);
  if (!sku) return; // no product SKU on this page — safe no-op

  // Gate the entire widget on `showVideos` (hidden from Public by default).
  // Suppress only on an EXPLICIT false so a features outage fails open.
  loadFeatures().then(function (features) {
    if (features && features.showVideos === false) return;
    // apiJson resolves to null on any non-ok/parse error — the same signal the
    // legacy script used to distinguish "request failed" (leave pane blank) from
    // "ok but empty" (render the empty-state message).
    apiJson('/api/parts/' + encodeURIComponent(sku), null, null).then(function (data) {
      if (data === null) return; // auth/server/404 — leave the pane blank
      var videos = Array.isArray(data.videos)
        ? data.videos.filter(function (v) { return v && v.url; })
        : [];
      var container = resolveContainer(explicit);
      if (!container) return;
      // Injecting content here is what reveals the shared plugin-tabs panel (via watchPluginTab).
      if (videos.length === 0) renderEmpty(container);
      else render(container, videos);
    });
  });
}

export var id = 'videos';
